
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function GardenPathwayLightingPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://electriciansolutionsfresno.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Garden & Pathway Lighting Installation',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional garden and pathway lighting installation in Fresno, CA. Safe, beautiful lighting for walkways, driveways, and garden paths.',
    url: `${siteUrl}/services/lighting-outdoor/garden-pathway-lighting`,
  };

  const features = [
    {
      icon: 'ri-footprint-line',
      title: 'Walkway Lighting',
      description: 'Safe, attractive lighting along front and back walkways',
    },
    {
      icon: 'ri-car-line',
      title: 'Driveway Lighting',
      description: 'Illuminate driveways for safe navigation after dark',
    },
    {
      icon: 'ri-plant-line',
      title: 'Garden Path Lights',
      description: 'Highlight garden paths and landscaping features',
    },
    {
      icon: 'ri-stairs-line',
      title: 'Step & Stair Lighting',
      description: 'Prevent trips and falls with step-mounted lights',
    },
    {
      icon: 'ri-contrast-2-line',
      title: 'Low Voltage Safety',
      description: 'Safe 12V systems perfect for ground-level installation',
    },
    {
      icon: 'ri-leaf-line',
      title: 'Energy Efficient LEDs',
      description: 'Long-lasting LED fixtures that reduce electricity costs',
    },
  ];

  return (
    <>
      <SEOHead
        title="Garden & Pathway Lighting Fresno CA | Walkway Lights"
        description="Professional garden and pathway lighting installation in Fresno, CA. Safe, beautiful lighting for walkways, driveways, and garden paths. Licensed electricians."
        canonical="/services/lighting-outdoor/garden-pathway-lighting"
        keywords="pathway lighting Fresno, garden lights, walkway lighting, driveway lights"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Garden & Pathway Lighting — Fresno, CA"
        heroSubtitle="Safe, beautiful pathway lighting for Fresno gardens, walkways, and driveways"
        heroImage="https://public.readdy.ai/ai/img_res/6e839758db5afa012798edb464c8467a.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20modern%20LED%20pathway%20light%20fixtures%20along%20stone%20garden%20walkway%20with%20warm%20amber%20glow%20illuminating%20path%20at%20night%20California%20property%20no%20people%20present&width=700&height=600&seq=pathway-light-fresno-009&orientation=portrait"
        fresnoContent="Enhance safety and beauty in your Fresno outdoor spaces with professional pathway lighting. From Tower District bungalows to Woodward Park estates, we install energy-efficient LED path lights that guide visitors safely along walkways, driveways, and garden paths. Our licensed electricians design low-voltage lighting systems that withstand Central Valley heat, provide even illumination without glare, and create inviting ambiance for your outdoor spaces. Enjoy your garden and pathways safely after dark year-round."
        features={features}
        faqs={[
          {
            question: 'How far apart should pathway lights be spaced in Fresno?',
            answer:
              'For Fresno pathways, we typically space lights 6–8 feet apart for even illumination without dark spots. Spacing depends on fixture brightness, path width, and desired ambiance. Narrow walkways (3–4 feet) work well with lights every 6 feet, while wider paths or driveways may need lights every 8–10 feet. We also consider landscaping—lights placed near plants or trees may need closer spacing. During consultation, we assess your specific pathways and design optimal spacing for safety and aesthetics.',
          },
          {
            question: "What type of pathway lights work best in Fresno's climate?",
            answer:
              "For Fresno's hot, dry climate, we recommend low-voltage LED pathway lights. LED fixtures handle extreme heat without performance loss, use 75% less energy than traditional bulbs, and last 25+ years. Low-voltage (12V) systems are safer and more energy-efficient than 120V systems. We install weather-resistant fixtures rated for temperatures exceeding 110°F. Popular styles include bollard lights for modern looks, mushroom lights for soft downward illumination, and stake lights for flexible placement in garden beds.",
          },
          {
            question: 'How much does pathway lighting installation cost in Fresno?',
            answer:
              'Pathway lighting installation in Fresno typically costs $1,500–$4,000 depending on path length and fixture quantity. Basic front walkway lighting with 6–8 fixtures starts around $1,200–$2,000. Comprehensive systems covering front walkway, driveway, and backyard paths with 15–20 fixtures range from $3,000–$5,000. Prices include low-voltage transformer, wiring, fixtures, and installation. We provide free consultations and detailed quotes for all Fresno projects. Most homeowners see immediate ROI through increased safety and property value.',
          },
          {
            question: 'Can pathway lights be installed in existing landscaping in Fresno?',
            answer:
              'Yes! We specialize in installing pathway lighting in existing Fresno landscapes with minimal disruption. Low-voltage systems require shallow trenching (4–6 inches deep) for wiring, which we carefully route around existing plants and irrigation. We restore any disturbed areas and ensure no damage to your landscaping. Many Fresno homeowners add pathway lighting years after initial landscaping. We work with your existing design to enhance, not disrupt, your outdoor spaces.',
          },
          {
            question: 'Should pathway lights be on timers or sensors in Fresno?',
            answer:
              "We recommend dusk-to-dawn photocell sensors for Fresno pathway lighting. These sensors automatically turn lights on at sunset and off at sunrise, ensuring your paths are always illuminated when needed without manual operation. This is especially convenient during Fresno's long summer evenings and short winter days. We also install timer systems for homeowners who prefer scheduled operation, and smart controls that allow app-based scheduling and remote control. All options save energy compared to lights left on 24/7.",
          },
          {
            question: 'Do pathway lights require permits in Fresno?',
            answer:
              "Low-voltage pathway lighting systems (12V) typically don't require permits in Fresno since they're considered low-risk. However, if your project involves new 120V circuits or significant electrical work, permits may be required by the City of Fresno. We handle all permit applications and inspections when needed, ensuring full compliance with Fresno building codes. Our licensed electricians ensure all installations meet safety requirements. Proper installation protects your home value and ensures insurance coverage.",
          },
        ]}
      />
    </>
  );
}
